import React, {useEffect, useRef, useState} from 'react'
import PropTypes from 'prop-types'
import {useIntl} from 'react-intl'
import {Box, Container} from '@salesforce/retail-react-app/app/components/shared/ui'
import {
    AuthHelpers,
    useAuthHelper,
    useCustomerBaskets,
    useCustomerId,
    useCustomerType
} from '@salesforce/commerce-sdk-react'
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'
import Seo from '../../components/seo'
import {useForm} from 'react-hook-form'
import {useRouteMatch} from 'react-router'
import {useLocation} from 'react-router-dom'
import useEinstein from '@salesforce/retail-react-app/app/hooks/use-einstein'
import LoginForm from '../../components/login'
import RegisterForm from '../../components/register'
import PasswordlessEmailConfirmation from '@salesforce/retail-react-app/app/components/email-confirmation/index'
import AuthTabs from '../../components/auth-tabs'
import {
    API_ERROR_MESSAGE,
    INVALID_TOKEN_ERROR,
    INVALID_TOKEN_ERROR_MESSAGE,
    LOGIN_TYPES,
    PASSWORDLESS_LOGIN_LANDING_PATH
} from '@salesforce/retail-react-app/app/constants'
import {isServer, noop} from '@salesforce/retail-react-app/app/utils/utils'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'

// Import centralized hooks
import {useLoginSubmit} from '../../hooks/use-login-submit'
import {useRegistrationSubmit} from '../../hooks/use-registration-submit'

import {GarageCollectif} from '../../components/icons'

const LOGIN_VIEW = 'login'
const EMAIL_VIEW = 'email'

/**
 * AuthPage - Unified page for both /login and /registration routes
 * Handles all authentication flows including login, registration, and passwordless login
 * Uses AuthTabs to provide seamless navigation without component re-mounting
 */
const AuthPage = ({initialView = LOGIN_VIEW}) => {
    const {formatMessage} = useIntl()
    const [mounted, setMounted] = useState(false)
    const navigate = useNavigation()
    const location = useLocation()
    const {pathname} = location
    const queryParams = new URLSearchParams(location.search)
    const {path} = useRouteMatch()
    const einstein = useEinstein()
    const {isRegistered} = useCustomerType()
    const loginPasswordless = useAuthHelper(AuthHelpers.LoginPasswordlessUser)

    // Configuration for login options
    const config = getConfig()
    const {passwordless = {}, social = {}} = config.app.login || {}
    const isPasswordlessEnabled = !!passwordless?.enabled
    const isSocialEnabled = !!social?.enabled
    const idps = social?.idps

    // Customer data
    const customerId = useCustomerId()
    const {data: baskets, isSuccess: isSuccessCustomerBaskets} = useCustomerBaskets(
        {parameters: {customerId}},
        {enabled: !!customerId && !isServer, keepPreviousData: true}
    )

    // Forms - separate forms for login and registration
    const loginForm = useForm()
    const registrationForm = useForm()

    // State management
    const [currentView, setCurrentView] = useState(initialView)
    const [redirectPath, setRedirectPath] = useState('')
    const routeStateRef = useRef(location.state)

    // Determine current form based on route
    const isLoginRoute = pathname === '/login'

    // Centralized login hook
    const loginSubmit = useLoginSubmit(loginForm, {
        context: 'login',
        onLoginSuccess: () => {
            // If the user was trying to access a protected route, redirect them there
            const redirectFrom = routeStateRef.current?.from?.pathname
            if (redirectFrom) {
                navigate(redirectFrom)
            }
        },
        onPasswordlessSuccess: () => {
            setCurrentView(EMAIL_VIEW)
        },
        onAccountNotVerified: () => {
            navigate('/email-confirm')
        },
        onSetCurrentView: setCurrentView
    })

    // Centralized registration hook
    const registrationSubmit = useRegistrationSubmit(
        registrationForm,
        // onSuccess - redirect to account
        noop,
        // onAccountVerificationRequired - redirect to email confirmation
        () => {
            navigate('/email-confirm')
        }
    )

    /**
     * Wrapper for login form submission
     */
    const handleLoginSubmit = (data) => {
        return loginSubmit.submitForm(data, currentView)
    }

    /**
     * Wrapper for registration form submission
     */
    const handleRegistrationSubmit = (data) => {
        return registrationSubmit.submitForm(data)
    }

    useEffect(() => {
        setMounted(true)
    }, [])

    /**
     * Effect to handle passwordless login via URL token
     */
    useEffect(() => {
        if (path === PASSWORDLESS_LOGIN_LANDING_PATH && isSuccessCustomerBaskets) {
            const token = decodeURIComponent(queryParams.get('token'))

            if (queryParams.get('redirect_url')) {
                setRedirectPath(decodeURIComponent(queryParams.get('redirect_url')))
            } else {
                setRedirectPath('')
            }

            const passwordlessLogin = async () => {
                try {
                    await loginPasswordless.mutateAsync({pwdlessLoginToken: token})
                } catch (e) {
                    const errorData = await e.response?.json()
                    const message = INVALID_TOKEN_ERROR.test(errorData.message)
                        ? formatMessage(INVALID_TOKEN_ERROR_MESSAGE)
                        : formatMessage(API_ERROR_MESSAGE)
                    loginForm.setError('global', {type: 'manual', message})
                }
            }
            passwordlessLogin()
        }
    }, [path, isSuccessCustomerBaskets])

    /**
     * Effect to handle post-authentication navigation
     */
    useEffect(() => {
        if (isRegistered) {
            // Handle basket merge for login flow
            if (isLoginRoute) {
                loginSubmit.handleMergeBasket()
            }

            // Navigate to intended destination
            const redirectTo = redirectPath || '/account'
            navigate(redirectTo)
        }
    }, [isRegistered, redirectPath, isLoginRoute])

    /**
     * Einstein analytics tracking
     */
    useEffect(() => {
        einstein.sendViewPage(pathname)
    }, [pathname])

    /**
     * Cleanup location state when navigating away from the auth page
     */
    useEffect(() => {
        if (!location.state) {
            return
        }
        routeStateRef.current = location.state
        const normalizedPath = `${location.pathname}${location.search}${location.hash}`
        navigate(normalizedPath, 'replace')
    }, [location.pathname, location.search, location.hash, location.state, navigate])

    const registrationPageTitle = formatMessage({
        id: 'registration.page.title',
        defaultMessage: 'Registration | Garage Clothing UK'
    })
    const registrationPageDescription = formatMessage({
        id: 'registration.page.description',
        defaultMessage: 'Register for Garage Clothing to track your orders, manage your wishlist and more.'
    })
    const loginPageTitle = formatMessage({
        id: 'login.page.title',
        defaultMessage: 'Sign in | Garage Clothing UK'
    })
    const loginPageDescription = formatMessage({
        id: 'login.page.description',
        defaultMessage: 'Customer sign in'
    })

    // Determine SEO content based on current route
    const seoTitle = isLoginRoute ? loginPageTitle : registrationPageTitle
    const seoDescription = isLoginRoute ? loginPageDescription : registrationPageDescription

    // Render login content
    const renderLoginContent = () => {
        // Show email confirmation view after successful passwordless login
        if (loginForm.formState.isSubmitSuccessful && currentView === EMAIL_VIEW) {
            return (
                <PasswordlessEmailConfirmation
                    form={loginForm}
                    submitForm={handleLoginSubmit}
                    email={loginSubmit.passwordlessLoginEmail}
                    isLoading={loginSubmit.isLoading}
                />
            )
        }

        // Show login form
        if (!loginForm.formState.isSubmitSuccessful && currentView === LOGIN_VIEW) {
            return (
                <LoginForm
                    form={loginForm}
                    submitForm={handleLoginSubmit}
                    clickCreateAccount={() => navigate('/registration')}
                    handlePasswordlessLoginClick={() => {
                        loginSubmit.setLoginType(LOGIN_TYPES.PASSWORDLESS)
                    }}
                    handleForgotPasswordClick={() => navigate('/reset-password')}
                    isPasswordlessEnabled={isPasswordlessEnabled}
                    isSocialEnabled={isSocialEnabled}
                    idps={idps}
                    setLoginType={loginSubmit.setLoginType}
                    isLoading={loginSubmit.isLoading}
                />
            )
        }

        return null
    }

    // Render registration content
    const renderRegistrationContent = () => {
        return (
            <RegisterForm
                submitForm={handleRegistrationSubmit}
                form={registrationForm}
                clickSignIn={() => navigate('/login')}
                isLoading={registrationSubmit.isLoading}
            />
        )
    }

    const seo = <Seo title={seoTitle}
        description={seoDescription} 
        openGraphTags={[{
                property: 'og:title',
                content: seoTitle
            } , {
                property: 'og:description',
                content: seoDescription
            }]}
        />

    if (!mounted) {
        return seo
    }

    return (
        <Box data-testid={isLoginRoute ? 'login-page' : 'registration-page'} bg="white">
            {seo}
            <Container
                width={['100%', '407px']}
                bg="white"
                pt={0}
                mt={{base: '54px', lg: '24px'}}
                px={0}
                paddingBottom={14}
                marginBottom={8}
                borderRadius="base"
            >
                <AuthTabs
                    loginContent={renderLoginContent()}
                    registrationContent={renderRegistrationContent()}
                />
            </Container>
        </Box>
    )
}

// Template name for server-side rendering
AuthPage.getTemplateName = () => 'auth'

AuthPage.propTypes = {
    /**
     * Initial view to display when component mounts
     */
    initialView: PropTypes.oneOf([LOGIN_VIEW, EMAIL_VIEW]),

    /**
     * Route match object from React Router
     */
    match: PropTypes.object
}

export default AuthPage
